import type { HTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

/**
 * rc136 — Primitivas de LISTA EN TARJETAS para móvil.
 *
 * Patrón acordado para la serie móvil (rc137+): cada listado decide con
 * `useIsMobile()` si pinta su <Table> de siempre (desktop) o una `MobileList`
 * de `MobileCard`s (teléfono). Las tarjetas no son una tabla apretada: cabecera
 * con título/subtítulo + un slot a la derecha (monto, estado) y debajo una
 * rejilla de `MobileMeta` (etiqueta chiquita arriba, valor abajo).
 *
 *   <MobileList>
 *     {rows.map(r => (
 *       <MobileCard key={r.id} to={`/clientes/${r.id}`} title={r.nombre}
 *                   subtitle={r.email} right={<Badge>{r.estado}</Badge>}>
 *         <MobileMeta label="Plan">{r.plan}</MobileMeta>
 *         <MobileMeta label="Saldo" mono>{money(r.saldo)}</MobileMeta>
 *       </MobileCard>
 *     ))}
 *   </MobileList>
 */
export function MobileList({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("space-y-2", className)} {...props} />;
}

type CardProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Slot derecho de la cabecera: monto, estado, chip. */
  right?: ReactNode;
  /** Navega como <Link> (toda la tarjeta es tocable). */
  to?: string;
  /** Estado de navegación para useBackLink ({ from, fromLabel }). */
  state?: unknown;
  onClick?: () => void;
  /** Acciones al pie (botones). Se renderizan fuera del área tocable. */
  actions?: ReactNode;
  /** Resalta la tarjeta (ej. seleccionada / urgente). */
  accent?: boolean;
  className?: string;
  children?: ReactNode;
};

export function MobileCard({ title, subtitle, right, to, state, onClick, actions, accent, className, children }: CardProps) {
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium leading-tight text-foreground">{title}</div>
          {subtitle && <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{subtitle}</div>}
        </div>
        {right && <div className="shrink-0 text-right text-[12px]">{right}</div>}
      </div>
      {children && <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">{children}</div>}
    </>
  );
  const base = cn(
    "block w-full border bg-card p-3 text-left transition-colors",
    actions ? "rounded-t-lg" : "rounded-lg",
    (to || onClick) && "active:bg-accent/60",
    accent && "border-l-2 border-l-electric",
  );
  return (
    <div className={cn("rounded-lg", className)}>
      {to ? (
        <Link to={to} state={state} className={base}>{inner}</Link>
      ) : onClick ? (
        <button type="button" onClick={onClick} className={cn(base, "tap-compact")}>{inner}</button>
      ) : (
        <div className={base}>{inner}</div>
      )}
      {actions && (
        <div className="-mt-px flex flex-wrap items-center justify-end gap-1.5 rounded-b-lg border border-t-0 bg-muted/30 px-2 py-1.5">
          {actions}
        </div>
      )}
    </div>
  );
}

export function MobileMeta({ label, mono, span, className, children }: { label: string; mono?: boolean; span?: boolean; className?: string; children: ReactNode }) {
  return (
    <div className={cn("min-w-0", span && "col-span-2", className)}>
      <div className="text-[9.5px] font-medium uppercase tracking-wider text-muted-foreground/70">{label}</div>
      <div className={cn("truncate text-[12px] text-foreground", mono && "font-mono tabular-nums")}>{children ?? "—"}</div>
    </div>
  );
}

/** Estado vacío para listas móviles (mismo tono que TableEmpty). */
export function MobileEmpty({ children }: { children: ReactNode }) {
  return <div className="rounded-lg border border-dashed py-10 text-center text-xs text-muted-foreground">{children}</div>;
}
